import { useRef } from 'react';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import {
  useCheckoutCartState,
  useEligibility,
} from '@monofront/business/contexts';
import { format } from '@monofront/business/utils';
import { COMPLETE_ORDER } from '@monofront/business/queries';
import { trackEvent } from '@monofront/tracking';
import {
  EmptyObject,
  FreeTrialStartedFromOriginEnum,
  ICompleteOrderQueryResult,
  MembershipPlan,
  PaymentMetadata,
} from '@monofront/types';

import { useAuthentication } from '@context/businessContainer';
import { useMembershipCouponContext } from '@context/membershipCoupon';
import { useProductInfosInCheckout } from '@hooks/businessContainer';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import { useCheckoutPayment } from '../contexts';
import { trackFreeTrialStarted } from '@utils/tracking';
import useHandlePaymentError from './useHandlePaymentErrors';
import { getRunningApp, getRunningPlatform } from '@utils/environment';
import {
  paymentMethodsTrackingValues,
  runPostOrderSideEffects,
} from '../utils/runPostOrderSideEffects';

type ISubscriptionParams =
  | EmptyObject
  | {
      withSubscription: boolean;
      subscriptionPromotionCode?: string;
      membershipPlan: MembershipPlan;
      paymentMetadata: PaymentMetadata;
    };

interface IUseCompleteOrderReturn {
  completeOrder: () => Promise<boolean>;
}

const useCompleteOrder = (): IUseCompleteOrderReturn => {
  const router = useRouter();
  const { authUser, customer } = useAuthentication();
  const { deleteCurrentShoppingCart } = useShoppingCart();
  const { checkoutCart, membershipPlan } = useCheckoutCartState();
  const { totalEconomy } = useProductInfosInCheckout();
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);
  const { validatedCoupon } = useMembershipCouponContext();
  const { setIsOrderValidationInProgress, selectedPaymentType } =
    useCheckoutPayment();
  const { handlePaymentError } = useHandlePaymentError();
  const { checkEligibility } = useEligibility();

  const [completeOrderMutation] =
    useMutation<ICompleteOrderQueryResult>(COMPLETE_ORDER);

  const inFlight = useRef<Promise<boolean> | null>(null);
  const hasSucceeded = useRef(false);

  const submitOrder = async () => {
    try {
      if (!checkoutCart?.id) {
        throw new Error('Cannot complete order without checkoutCart id.');
      }

      let withSubscriptionParams: ISubscriptionParams = {};

      if (isSubscriptionAvailable) {
        withSubscriptionParams = {
          withSubscription: true,
          subscriptionPromotionCode: validatedCoupon.code || undefined,
          membershipPlan,
          paymentMetadata: {
            app: getRunningApp(),
            platform: getRunningPlatform(),
          },
        };
      }

      const completeOrderResponse = await completeOrderMutation({
        variables: {
          tokenValue: checkoutCart.id,
          ...withSubscriptionParams,
        },
      });

      // The order is placed from here on: OMS has assigned its number and queued
      // the capture. A throw below must never reach the outer catch, which tells
      // the customer they were not charged — they act on that and pay again.
      try {
        // Null on a cart the completion just consumed, which is exactly when this
        // runs. Dereferencing it unguarded is what turned every success into a
        // "cannotCompleteOrder" toast and a duplicate paid order.
        if (checkoutCart.shippingAddress) {
          checkEligibility({
            street: checkoutCart.shippingAddress.street,
            city: checkoutCart.shippingAddress.city,
            postalCode: checkoutCart.shippingAddress.postcode,
            country: checkoutCart.shippingAddress.countryCode,
          });
        }

        if (isSubscriptionAvailable) {
          if (completeOrderResponse.data?.completeOrder?.subscriptionId) {
            trackFreeTrialStarted({
              subscriptionId:
                completeOrderResponse.data.completeOrder.subscriptionId,
              email: authUser?.email,
              promotionCode: validatedCoupon,
              freeTrialStartType: FreeTrialStartedFromOriginEnum.checkout,
              isMembershipAsAProduct: true,
              freeTrialPaymentMethod: selectedPaymentType
                ? paymentMethodsTrackingValues[selectedPaymentType]
                : 'unknown',
            });
          } else {
            handlePaymentError({
              code: 'MAAP_SUBS_FAILED',
              message:
                'Subscription was not created during completeOrder (MAAP flow).',
            });
          }
        }

        // Awaited: the 3DS path already does. Unawaited, navigation can win the race
        // and leave the Firestore cart populated, which is the ammunition a
        // back-navigation to /checkout/payment needs to mint a second order.
        await deleteCurrentShoppingCart();

        if (membershipPlan === MembershipPlan.MONTHLY) {
          trackEvent({
            type: 'LFStartMonthlySubscription',
            data: {
              subscription_id:
                completeOrderResponse.data?.completeOrder?.subscriptionId,
              subscription_email: authUser?.email as string,
              coupon_name: validatedCoupon.code,
              coupon_id: validatedCoupon.id,
            },
          });
        }

        const {
          pastSavingsAmount,
          newSavingsAmount,
          isFirstOrder,
          isFreeTrialStart,
        } = runPostOrderSideEffects({
          checkoutCart,
          customer,
          totalEconomy,
          isSubscriptionAvailable,
          selectedPaymentType,
        });

        // lastOrderSavings param will not appear in url
        router.push(
          {
            pathname: '/checkout/confirmation',
            query: {
              savings: format(newSavingsAmount),
              pastSavingsAmount,
              newSavingsAmount,
              isFirstOrder,
              'cs-free-trial-start': isFreeTrialStart,
            },
          },
          `/checkout/confirmation`,
        );
      } catch (error: any) {
        // Logged, not displayed: the payment went through. Still send the customer
        // to the confirmation page rather than leaving them on payment.
        handlePaymentError({
          code: 'POST_ORDER_SIDE_EFFECT_FAILED',
          message: 'Order completed but a post-order side effect failed.',
          error,
        });

        router.push('/checkout/confirmation');
      }

      return true;
    } catch (error: any) {
      setIsOrderValidationInProgress(false);

      const code =
        error?.graphQLErrors?.[0]?.code === 'ORDER_OUT_OF_STOCK'
          ? 'ORDER_OUT_OF_STOCK'
          : 'COMPLETE_ORDER_FAILED';

      handlePaymentError({
        code,
        message:
          !error?.graphQLErrors && error?.message
            ? error.message
            : 'Cannot complete order.',
        shouldDisplayError: true,
        error,
      });
    }

    return false;
  };

  /**
   * Completing an order is not idempotent: OMS assigns the number and queues the
   * payment capture on the first call, so a second one lands on an order that is
   * already completed, gets a 400/404, and shows the customer a payment error
   * they will act on. Every caller goes through this latch — the PayPal CTAs
   * submit straight from SDK callbacks and have no state machine to rely on.
   */
  const completeOrder = async (): Promise<boolean> => {
    if (hasSucceeded.current) {
      return true;
    }

    // Share the first attempt rather than reporting a failure to the second
    // caller, which would surface an error for an order that is still going.
    if (inFlight.current) {
      return inFlight.current;
    }

    const attempt = submitOrder();

    inFlight.current = attempt;

    try {
      const hasCompleted = await attempt;

      // Only a success latches, so a genuine retry after a real failure works.
      hasSucceeded.current = hasCompleted;

      return hasCompleted;
    } finally {
      inFlight.current = null;
    }
  };

  return {
    completeOrder,
  };
};

export default useCompleteOrder;
